import React, { useEffect } from "react";
import {
    Alert,
    AppBar,
    Avatar,
    Backdrop,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Modal,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { LoadState, assertThumbnailUrl, getBackendUrl } from "../../../redux/common";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { FaceDetailsInfo, faceDisplayname } from "../../../redux/face/faceDetailsSlice";
import { faceSetNobodySlice, requestSetFaceNobody } from "../../../redux/face/faceSetNobodySlice";
import { faceQuerySlice, retrieveFaceQuery } from "../../../redux/face/faceQuerySlice";
import { faceSetSomebodySlice, requestSetFaceSomebody, selectFaceSetSomebody } from "../../../redux/face/faceSetSomebodySlice";
import { retrieveSuggestionDetailsList, suggestionDetailsListSlice } from "../../../redux/face/suggestion/suggestionDetailsListSlice";
import { SuggestionDetailsInfo, suggestionDisplayname } from "../../../redux/face/suggestion/suggestionDetailsSlice";
import { stringToColor } from "../../user/avatarUtils";

export interface Buttons {
    label: React.ReactNode;
    click: () => void;
}
  
export interface ModalImageDetailMgmtProps {
    open: boolean;
    close: () => void;
    previous?: () => void;
    next?: () => void;
    image: FaceDetailsInfo | undefined;
    schoolId?: string;
}

export function ModalImageDetailMgmt(props: ModalImageDetailMgmtProps) {
    const dispatch = useAppDispatch();
    const faceSetNobody = useAppSelector(state => state.faceSetNobody);
    const faceSetSomebody = useAppSelector(selectFaceSetSomebody);
    const faceQuery = useAppSelector(state => state.faceQuery);
    const suggestions = useAppSelector(state => state.suggestionDetailsList);

    useEffect(() => {
        if(props.image?.photoFace.id != null) {
            dispatch( suggestionDetailsListSlice.actions.check({ faceId: props.image?.photoFace.id }) );
        }
    }, [props.image]);

    useEffect(() => {
        if(suggestions.state.state == LoadState.NotStarted && props.image?.photoFace.id) {
            dispatch( retrieveSuggestionDetailsList({ faceId: props.image.photoFace.id }) );
        }
    }, [suggestions.state.state, props.image]);

    if(props.image == undefined) {
        return <></>;
    }    

    const setNobody = () => {
        if(props.image) {
            dispatch( requestSetFaceNobody({ face: props.image.photoFace }) );
        }
    };
    const setSomebody = (event: any, target: "student" | "user", targetId: string) => {
        event.preventDefault();
        if(props.image) {
            dispatch( requestSetFaceSomebody({ face: props.image.photoFace, target: target, targetId: targetId }) );
        }
    };

    const elToolbar = <AppBar position="static" color={"primary"} sx={{ top: "auto", bottom: 0 }}>
        <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}> {faceDisplayname(props.image)} </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <ButtonGroup variant="contained" color="secondary">
                <Tooltip title="Fechar" >
                    <Button key="close" onClick={(/* event */) => { props.close(); }}><CloseIcon /></Button>
                </Tooltip>
            </ButtonGroup>
        </Toolbar>
    </AppBar>;

    const landscape = window.innerHeight < window.innerWidth;

    const modalMargin = 15;
    const buttonWidth = 40;
    const buttonHeight = 35;

    const contentBorder = "";// "1px solid #000000";
    const contentXMargin = landscape ? 0 : 1;
    const contentHeigth = landscape 
    ? window.innerHeight - ( 80 )
    : window.innerHeight - ( 80 + buttonHeight )
    ;
    const contentWidth = landscape
    ? window.innerWidth - (contentXMargin*2 + modalMargin*2 + buttonWidth*2)
    : window.innerWidth - (contentXMargin*2 + modalMargin*2)
    ;

    const url = `${getBackendUrl()}/api/face/${props.image.photoFace.id}`;
    const content = () => {
        const changed = (event: React.ChangeEvent<HTMLInputElement>) => {
            event.preventDefault();
            if(!props.schoolId) {
                console.error("School ID is required for face query.");
                return;
            }
            if(`${event.target.value}` == "") {
                dispatch( faceQuerySlice.actions.clean() );
                return;
            }
            dispatch( retrieveFaceQuery({ schoolId: props.schoolId, filter: event.target.value }) );
        };
    
        const options = [] as React.ReactNode[];
        const students = faceQuery.response?.students?.filter((student) => student.name != null && !`${student.name}`.startsWith("Deleted")) || [];
        for(const answer of students) {
            const student = answer;
            const name = student.nickname || student.name;
            const imageUrl = assertThumbnailUrl( student.url );
            options.push(
                <Tooltip title={name}>
                    <div onClick={(event) => { setSomebody(event, "student", student.id); }}>
                        <Stack direction="column" key={`student:${student.id}`}>
                            <Avatar sx={{height: 60, width: 60, backgroundColor: stringToColor(name)}} alt={name} src={`${imageUrl}`} />
                            <Typography variant="caption" component="span" 
                                sx={{width: 60, overflow: "hidden", textOverflow: "ellipsis", display: "block", textWrap: "nowrap", textAlign: "center"}}>
                                {name}
                            </Typography>
                        </Stack>
                    </div>
                </Tooltip>
            );
        }
        const users = faceQuery.response?.users?.filter((user) => !`${user.name}`.startsWith("Deleted")) || [];
        for(const answer of users) {
            const user = answer;
            const name = user.name || user.email;
            const imageUrl = assertThumbnailUrl( user.url );
            options.push(
                <Tooltip title={name}>
                    <div onClick={(event) => { setSomebody(event, "user", user.id); }}>
                        <Stack direction="column" key={`user:${user.id}`}>
                            <Avatar sx={{height: 60, width: 60, backgroundColor: stringToColor(name)}} alt={name} src={`${imageUrl}`} />
                            <Typography variant="caption" component="span" 
                                sx={{width: 60, overflow: "hidden", textOverflow: "ellipsis", display: "block", textWrap: "nowrap", textAlign: "center"}}>
                                {name}
                            </Typography>
                        </Stack>
                    </div>
                </Tooltip>
            );
        }

        const acceptSuggestion = (event: any, item: SuggestionDetailsInfo) => {
            if(item.student) {
                setSomebody(event, "student", item.student.id);
            } else if(item.user) {
                setSomebody(event, "user", item.user.id);
            } else {
                console.log("deu ruim 2");
            }
        };

        const suggestionList = suggestions.list?.filter(item => item.student != undefined || item.user != undefined) || [];

        const suggestionsEl = (suggestionList.length > 0)
            ? suggestionList.map((item) => {
                const name = suggestionDisplayname(item);
                const imageUrl = assertThumbnailUrl( item.student?.url || item.user?.url );
                return <Tooltip key={name} title={name}>
                    <div onClick={(e) => acceptSuggestion(e, item)}>
                        <Stack direction="column" key={item.suggestedFace.suggestedFaceId}>
                            <Avatar sx={{height: 60, width: 60, backgroundColor: stringToColor(name)}} alt={name} src={`${imageUrl}`} />
                            <Typography variant="caption" component="span" 
                                sx={{width: 60, overflow: "hidden", textOverflow: "ellipsis", display: "block", textWrap: "nowrap", textAlign: "center"}}>
                                {name}
                            </Typography>
                        </Stack>
                    </div>
                </Tooltip>;
            })
            :  <Typography variant="body1" component="div" sx={{ p: 2 }}>Nenhuma sugestão</Typography>
            ;

        return <Box>
            <Stack direction={ landscape ? "row" : "column" } width="100%">
                <Stack sx={{display: !landscape ? "block" : "none"}} direction="row" width="100%">
                    <Button sx={{width: "50%", minWidth: "50%"}} variant="outlined" color="primary" onClick={() => props.previous ? props.previous() : {}} disabled={props.previous ? false : true}><ArrowBackIosNewIcon/></Button>
                    <Button sx={{width: "50%", minWidth: "50%"}} variant="outlined" color="primary" onClick={() => props.next ? props.next() : {}} disabled={props.next ? false : true}><ArrowForwardIosIcon/></Button>
                </Stack>
                <Button sx={{width: buttonWidth, minWidth: buttonWidth, display: landscape ? "block" : "none"}} onClick={() => props.previous ? props.previous() : {}} disabled={props.previous ? false : true}><ArrowBackIosNewIcon/></Button>
                <Box  sx={{ height: contentHeigth, width: contentWidth, objectFit: "contain", mx: `${contentXMargin}px`, border: contentBorder }}>
                    <Stack direction={ landscape ? "row" : "column" } width="100%" alignItems={ landscape ? "" : "center" }>
                        <Stack direction={ landscape ? "column" : "row" } sx={{width: landscape ? 150 : "90%" }} alignItems={"center"}>
                            <Box
                                component="img"
                                sx={{ height: 100, width: 100, objectFit: "contain", my: 2 }}
                                alt={faceDisplayname(props.image)}
                                src={url} />
                            <Tooltip title="Desconhecido" >
                                <Button key="nobody" sx={{mx: 2}} variant="contained" color="error" onClick={(/* event */) => { setNobody(); }}>Desconhecido</Button>
                            </Tooltip>
                            <Typography variant="body2" component="div" sx={{ flexGrow: 1, textAlign: "center" }}>
                                Face não reconhecida ou não é uma pessoa
                            </Typography>
                        </Stack>
                        <Box sx={{ flexGrow: 1, p: 2, width: "100%" }}>
                            <Stack direction="column" spacing={2} width="100%">
                                <Paper elevation={3} sx={{ p: 1, width: "100%", overflowX: "auto" }}>
                                    <Typography variant="h6" component="div">Sugestões</Typography>
                                    <Stack direction="row" spacing={2}>
                                        {suggestionsEl}
                                    </Stack>
                                </Paper>
                                <TextField required id="filter" name="filter" label="Buscar"
                                    fullWidth autoComplete="filter" variant="standard"
                                    value={faceQuery.request?.filter}
                                    onChange={changed} />
                                <Stack direction="row" spacing={1}>
                                    {options}
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
                <Button sx={{width: buttonWidth, minWidth: buttonWidth, display: landscape ? "block" : "none"}} onClick={() => props.next ? props.next() : {}} disabled={props.next ? false : true}><ArrowForwardIosIcon/></Button>
            </Stack>
        </Box>;
    };

    const working = faceSetNobody.state.state == LoadState.Loading 
        || faceSetSomebody.state.state == LoadState.Loading
        || faceQuery.state.state == LoadState.Loading
        ;

    const failed = faceSetNobody.state.state == LoadState.Failed
        || faceSetSomebody.state.state == LoadState.Failed
        || faceQuery.state.state == LoadState.Failed
        ;

    const dismissError = () => {
        if( faceSetNobody.state.state == LoadState.Failed ) {
            dispatch( faceSetNobodySlice.actions.clean() );
        }
        if( faceSetSomebody.state.state == LoadState.Failed ) {
            dispatch( faceSetSomebodySlice.actions.clean() );
        }
        if( faceQuery.state.state == LoadState.Failed ) {
            dispatch( faceQuerySlice.actions.clean() );
        }
    };

    return <Modal open={props.open} onClose={() => {/*props.close();*/}} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Paper sx={{ m: `${modalMargin}px`, width: window.innerWidth, minWidth: "300px", height: window.innerHeight-modalMargin*2 }} elevation={3}>
            {elToolbar}
            <Box display={"block"} width="100%">
                {content()}
            </Box>
            <Backdrop open={working} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar open={failed} autoHideDuration={1000} onClose={dismissError}>
                <Alert onClose={dismissError} severity="error" variant="filled" sx={{ width: "100%" }}>
                    Erro ao executar operação.
                </Alert>
            </Snackbar>
        </Paper>
    </Modal>;
}
